import { AmountType, RuleChange, RuleCondition } from '../../../cashflow';

export type Enabled = { enabled: boolean };

// Adding an enabled boolean allows us to keep the condition/change object values when it's toggled off
export type RuleConditionWithEnabled = RuleCondition & Enabled;
export type RuleChangeWithEnabled = RuleChange & Enabled;

export interface ConditionConfig {
    label: string;
    field: RuleCondition['field'];
    options: {
        value: RuleCondition['matcher'];
        label: string;
    }[];
    defaultValue: RuleConditionWithEnabled;
}

export interface ChangeConfig {
    label: string;
    field: RuleChange['field'];
    defaultValue: RuleChangeWithEnabled;
}

export const AMOUNT_TYPE_OPTIONS: { label: string; value: AmountType }[] = [
    {
        label: 'Spending',
        value: 'spending',
    },
    {
        label: 'Income',
        value: 'income',
    },
];

export const CONDITION_CONFIGS: ConditionConfig[] = [
    {
        label: 'Merchant name',
        field: 'merchantName',
        options: [
            {
                label: 'Exactly matches',
                value: 'equalToInsensitive',
            },
            {
                label: 'Includes',
                value: 'includesInsensitive',
            },
        ],
        defaultValue: {
            field: 'merchantName',
            matcher: 'equalToInsensitive',
            value: '',
            enabled: false,
        },
    },
    {
        label: 'Transaction name',
        field: 'statement',
        options: [
            {
                label: 'Exactly matches',
                value: 'equalToInsensitive',
            },
            {
                label: 'Includes',
                value: 'includesInsensitive',
            },
        ],
        defaultValue: {
            field: 'statement',
            matcher: 'equalToInsensitive',
            value: '',
            enabled: false,
        },
    },
    {
        label: 'Subcategory',
        field: 'categoryId',
        options: [
            {
                label: 'Is',
                value: 'equalTo',
            },
        ],
        defaultValue: {
            field: 'categoryId',
            matcher: 'equalTo',
            value: '',
            enabled: false,
        },
    },
    {
        label: 'Amount',
        field: 'amount',
        options: [
            {
                label: 'Equals',
                value: 'equalTo',
            },
            {
                label: 'Less than',
                value: 'lessThan',
            },
            {
                label: 'Greater than',
                value: 'greaterThan',
            },
            {
                label: 'Between',
                value: 'between',
            },
        ],
        defaultValue: {
            field: 'amount',
            matcher: 'equalTo',
            value: 0,
            enabled: false,
            amountType: 'spending',
        },
    },
    {
        label: 'Account',
        field: 'financialEntityId',
        options: [
            {
                label: 'Is',
                value: 'equalTo',
            },
        ],
        defaultValue: {
            field: 'financialEntityId',
            matcher: 'equalTo',
            value: '',
            enabled: false,
        },
    },
];

export const CONDITION_CONFIG_MAP = CONDITION_CONFIGS.reduce(
    (acc, config) => {
        acc[config.field] = config;
        return acc;
    },
    {} as Record<ConditionConfig['field'], ConditionConfig>
);

export const CHANGE_CONFIGS: ChangeConfig[] = [
    {
        label: 'Update merchant',
        field: 'merchantId',
        defaultValue: {
            field: 'merchantId',
            value: '',
            enabled: false,
        },
    },
    {
        label: 'Update subcategory',
        field: 'categoryId',
        defaultValue: {
            field: 'categoryId',
            value: '',
            enabled: false,
        },
    },
    {
        label: 'Add tags',
        field: 'tagNames',
        defaultValue: {
            field: 'tagNames',
            value: [],
            enabled: false,
        },
    },
    {
        label: 'Hide transaction',
        field: 'isHidden',
        defaultValue: {
            field: 'isHidden',
            value: false,
            enabled: false,
        },
    },
];

export const CHANGE_CONFIG_MAP = CHANGE_CONFIGS.reduce(
    (acc, config) => {
        acc[config.field] = config;
        return acc;
    },
    {} as Record<ChangeConfig['field'], ChangeConfig>
);
